import { ThirdPartyRefundProvider } from "@redotech/cockroachdb-codegen/congodb/third-party-refund-provider";
import { Currency } from "@redotech/money/currencies";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useTranslation } from "@redotech/redo-i18n";
import {
  availableRefundDestinationProviders,
  CustomerRefundProvider,
  isRefundDestinationProvider,
  OrderContact,
  orderContactFromReturn,
  recipientFieldForProvider,
  RefundRecipientField,
} from "@redotech/redo-return-core-common/third-party-refunds/recipient";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { RpcClientContext } from "../../contexts/rpc";
import { CustomerPortalAppSettings } from "../../settings";
import {
  RefundDestinationForm,
  useRefundDestinationForm,
} from "./use-refund-destination-form";

/** The subset of a loaded return this hook needs; anything wider is noise. */
export interface RefundDestinationReturn {
  currency?: Currency | null;
  customer?: { email?: string | null; phone_number?: string | null } | null;
  shipping_address?: { email?: string | null; phone?: string | null } | null;
}

export function returnRefundDestinationQueryKey(returnId: string) {
  return ["returnRefundDestination", returnId];
}

/**
 * The refund destination of a return that has already been submitted. Unlike
 * the draft-return picker this saves on an explicit action, because a customer
 * who opens the editor and changes their mind should be able to walk away.
 */
export function useReturnRefundDestination({
  returnId,
  return_,
  settings,
  enabled = true,
}: {
  returnId: string;
  return_: RefundDestinationReturn | undefined;
  settings: CustomerPortalAppSettings | undefined;
  enabled?: boolean;
}) {
  const { t } = useTranslation();
  const rpcClient = useRequiredContext(RpcClientContext);
  const queryClient = useQueryClient();
  const [saveError, setSaveError] = useState<string | undefined>(undefined);

  const destinationQuery = useQuery({
    queryKey: returnRefundDestinationQueryKey(returnId),
    queryFn: async () =>
      (await rpcClient.getReturnRefundDestination({ returnId })).destination,
    enabled: enabled && !!returnId,
  });
  const destination = destinationQuery.data ?? undefined;

  // A bank account can be switched away from but not (re)entered here, so it
  // is listed only while it is the saved destination.
  const providers: CustomerRefundProvider[] =
    availableRefundDestinationProviders(settings?.redoRefundProviders);
  if (destination?.provider === ThirdPartyRefundProvider.Stripe) {
    providers.push(ThirdPartyRefundProvider.Stripe);
  }

  const orderContact: OrderContact | undefined = useMemo(
    () => (return_ ? orderContactFromReturn(return_) : undefined),
    [return_],
  );

  const form = useRefundDestinationForm({
    // Post-submission the customer is past opting out entirely, so a rail the
    // team no longer offers falls through to one that can still pay rather
    // than leaving nothing selected.
    originalPaymentAvailable: false,
    providers,
    persistedProvider: destination?.provider ?? null,
    persistedRecipient: destination?.recipient,
    orderContact,
  });

  const saveMutation = useMutation({
    mutationFn: async () => {
      const provider = form.selectedProvider;
      if (!isRefundDestinationProvider(provider) || !form.resolved?.ok) {
        throw new Error("Refund destination is not ready to save");
      }
      const value = form.resolved.normalized;
      return await rpcClient.updateReturnRefundDestination({
        returnId,
        provider,
        recipient:
          recipientFieldForProvider(provider) === RefundRecipientField.PHONE
            ? { phone: value }
            : { email: value },
      });
    },
    onSuccess: (result) => {
      queryClient.setQueryData(
        returnRefundDestinationQueryKey(returnId),
        result.destination,
      );
      // The rail is mirrored onto the return itself, so its cache is stale too.
      void queryClient.invalidateQueries({
        queryKey: ["returnConfirmation", returnId],
      });
      form.reset();
    },
  });

  const save = useHandler(async (): Promise<boolean> => {
    setSaveError(undefined);
    try {
      await saveMutation.mutateAsync();
      return true;
    } catch {
      setSaveError(
        t("We couldn't save your refund destination. Please try again."),
      );
      return false;
    }
  });

  /** Drops a pending edit; call when the editor is dismissed without saving. */
  const cancel = useHandler(() => {
    setSaveError(undefined);
    form.reset();
  });

  return {
    form: form satisfies RefundDestinationForm,
    destination,
    loading: destinationQuery.isPending,
    /** The team offers a rail and this return is on one. */
    isAvailable: providers.length > 0 && !!destination,
    canEdit: !!destination?.canEdit,
    save,
    cancel,
    saving: saveMutation.isPending,
    /** Belongs under the recipient field. */
    validationMessage: form.validationMessage,
    /** Belongs on the modal: a failed save is not a bad field. */
    saveError,
  };
}
